import { useState, useEffect, useRef, useCallback } from 'react'
import { Handle, Position } from 'reactflow'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { DataNodeDialog } from '@/components/ui/data-node-dialog'
import { Database, Maximize2, FileText, Image as ImageIcon, File, Loader2 } from 'lucide-react'

interface DataNodeProps {
  data: {
    id: string
    content: string
    metadata: {
      type?: string
      isUrl?: boolean
      filename?: string
      mimeType?: string
      status?: string
      output?: string
      error?: string
      inngestEventId?: string
    }
  }
  selected: boolean
  draggable?: boolean
}

export function DataNode({
  data,
  selected,
  draggable = true,
}: DataNodeProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dragging, setDragging] = useState(false)
  const dragRef = useRef<HTMLDivElement>(null)

  // Get appropriate icon based on mime type
  const getFileIcon = () => {
    if (!data.metadata.mimeType) return <File className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
    if (data.metadata.mimeType.startsWith('text/')) return <FileText className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
    if (data.metadata.mimeType.startsWith('image/')) return <ImageIcon className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
    return <Database className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
  }

  // Handle drag events
  useEffect(() => {
    const node = dragRef.current
    if (!node) return

    const handleDragStart = () => setDragging(true)
    const handleDragEnd = () => setDragging(false)

    node.addEventListener('dragstart', handleDragStart)
    node.addEventListener('dragend', handleDragEnd)

    return () => {
      node.removeEventListener('dragstart', handleDragStart)
      node.removeEventListener('dragend', handleDragEnd)
    }
  }, [])

  // Handle node status updates
  useEffect(() => {
    console.log('[DataNode] Status update:', {
      id: data.id,
      status: data.metadata?.status,
      inngestEventId: data.metadata?.inngestEventId,
      metadata: {
        ...data.metadata,
        output: data.metadata?.output?.substring(0, 100) + '...'
      }
    })

    const status = data.metadata?.status
    if (status === 'processing') {
      setIsLoading(true)
      setError(null)
    } else if (status === 'error') {
      setIsLoading(false)
      setError(data.metadata?.error || 'Processing failed')
    } else if (status === 'complete') {
      setIsLoading(false)
      setError(null)
    }
  }, [data.metadata?.status, data.metadata?.error, data.metadata?.inngestEventId])

  // Get content preview based on mime type
  const getContentPreview = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Processing...</span>
        </div>
      )
    }

    if (error) {
      return (
        <div className="text-sm text-destructive">
          {error}
        </div>
      )
    }

    // Show processed output if available
    if (data.metadata?.output) {
      return (
        <div className="text-sm text-muted-foreground line-clamp-3">
          {data.metadata.output}
        </div>
      )
    }

    // Show image preview for images
    if (data.metadata.mimeType?.startsWith('image/')) {
      return (
        <div className="relative aspect-video rounded-lg overflow-hidden bg-secondary/10">
          <img
            src={data.content}
            alt={data.metadata.filename}
            className="w-full h-full object-cover"
          />
        </div>
      )
    }

    // Default preview
    return (
      <div className="text-sm text-muted-foreground line-clamp-3">
        {data.content}
      </div>
    )
  }

  // Pass metadata to dialog
  const handleOpenDialog = useCallback(() => {
    console.log('[DataNode] Opening dialog with metadata:', {
      ...data.metadata,
      output: data.metadata?.output?.substring(0, 100) + '...'
    })
    setDialogOpen(true)
  }, [data.metadata])

  return (
    <div
      ref={dragRef}
      className={cn(
        'group relative p-4 rounded-lg border bg-card shadow-sm transition-all duration-200',
        'hover:shadow-md hover:scale-[1.02]',
        'active:scale-100 active:shadow-sm',
        selected && 'ring-2 ring-primary',
        dragging && 'opacity-50',
        error ? 'border-destructive/50' : 'border-border'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      draggable={true}
      style={{
        cursor: 'grab',
        touchAction: 'none'
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        className="!bg-muted-foreground"
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!bg-muted-foreground"
      />

      <Card
        className={cn(
          "p-3 min-w-[200px] max-w-[300px] bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
          selected && "ring-2 ring-muted",
          isHovered && "ring-1 ring-muted"
        )}
      >
        <div className="space-y-4">
          {/* File Info */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {getFileIcon()}
              <span className="text-sm font-medium truncate">
                {data.metadata.filename}
              </span>
            </div>
            <button
              onClick={handleOpenDialog}
              className="p-1 rounded-md hover:bg-accent"
            >
              <Maximize2 className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          {/* Content Preview */}
          {getContentPreview()}
        </div>
      </Card>

      {/* Dialog */}
      <DataNodeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        filename={data.metadata.filename}
        content={data.content}
        mimeType={data.metadata.mimeType}
        metadata={data.metadata}
      />
    </div>
  )
}
